import React, { FC, useRef, useState, useEffect } from 'react';
import classnames from 'classnames/bind';

import styles from './styles.module.css';
import { useHover } from '../../hooks/useHover/useHover';

const cx = classnames.bind(styles);

type TooltipProps = {
  /**
   * @param {React.ReactNode} tooltip The text or component that will appear when the children is hovered
   */
  tooltip: React.ReactNode;

  /**
   * @param {string} [position='top'] where the popover should appear relative to the trigger
   */
  position?: 'top' | 'bottom' | 'left' | 'right';

  /**
   * @param {string} testId becomes data-testid
   */
  testId?: string;

  /**
   * @param {string} className
   */
  className?: string;
  children?: React.ReactNode;
};

export const Tooltip: FC<TooltipProps> = ({
  children,
  position = 'top',
  tooltip,
  className,
  testId,
}) => {
  const { isHovered, onHoverProps } = useHover();
  const popoverRef = useRef<HTMLDivElement>(null);

  const [tooltipPositionFixes, changeTooltipPositionFixes] = useState<
    React.CSSProperties
  >({});

  useEffect(() => {
    const docWidth = window.innerWidth;
    const rect = popoverRef.current?.getBoundingClientRect();
    if (rect) {
      const rightEdge = rect.x + rect.width;

      if (rightEdge > docWidth) {
        const overhang = rightEdge - docWidth;
        const scrollbar = 34;
        changeTooltipPositionFixes({
          left: `calc(50% - ${overhang + scrollbar}px`,
        });
        return;
      }
    }

    changeTooltipPositionFixes({});
  }, [isHovered]);

  return (
    <div data-testid={testId} className={cx('wrapper', className)}>
      <div
        data-testid="tooltip-trigger"
        className={cx('trigger')}
        {...onHoverProps}
      >
        {children}
        {isHovered && (
          <div
            ref={popoverRef}
            data-testid="tooltip-popover-wrapper"
            className={cx('popover-wrapper', position)}
            style={tooltipPositionFixes}
            {...onHoverProps}
          >
            <div data-testid="tooltip-popover" className={cx('popover')}>
              {tooltip}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
